import { API_BASE_URL, STAGE } from '../config';

export interface CryptoPaymentInfo {
  address: string;
  amount: number;
  usdValue: number;
  expiresAt: number;
  status: 'pending' | 'paid' | 'expired';
  paidMethod: 'XCH' | 'STR';
}

export interface StripePaymentInfo {
  usdValue: number;
  status: 'pending' | 'paid';
  checkoutUrl: string;
}

export interface WalletCreateResponse {
  status: 'success' | 'error';
  walletId: number;
  userId: string;
  valid: boolean;
  exists: boolean;
  paid: boolean;
  walletStatus: 'pending' | 'active';
  cryptoPayment?: CryptoPaymentInfo;
  stripePayment?: StripePaymentInfo;
}

export interface PaymentInfo {
  address: string;
  amount: number;
  usdValue: number;
  expiresAt: number;
  status: string;
  paidMethod: 'STRIPE' | 'XCH' | 'STR';
}

export interface Wallet {
  walletId: number;
  masterKey: string;
  userId: string;
  paid: boolean;
  expired: boolean;
  addressesGenerated: boolean;
  generationProgress?: number;
  paymentInfo?: PaymentInfo;
}

export interface WalletSettingsResponse {
  status: string;
  message: string;
  walletId: number;
  updates: {
    threshold?: number;
    enabled?: boolean;
    removed?: boolean;
  };
}

export interface WalletSettings {
  walletId: number;
  masterKey: string;
  threshold: number;
  enabled: boolean;
  removed?: boolean;
}

export interface WalletSettingsUpdate {
  threshold?: number;
  enabled?: boolean;
  removed?: boolean;
}

export interface WalletListResponse {
  status: string;
  wallets: Wallet[];
}

export interface WalletInfo {
  walletId: number;
  masterKey: string;
  status: string;
  threshold: number;
  enabled: boolean;
  validTill: string;
  addressesGenerated: boolean;
  generationProgress?: number;
}

export interface WalletEvent {
  id: string;
  walletId: number;
  timestamp: number;
  blockHeight: number;
  blockTime: number;
  data: {
    xch?: {
      aggregatedAmount: number;
      receivedCoins: string[];
      spentCoins: string[];
    };
    tokens?: Array<{
      aggregatedAmount: number;
      tokenId: string;
      receivedCoins: string[];
      spentCoins: string[];
    }>;
    nfts?: {
      sent: string[];
      received: string[];
    };
  };
}

export interface EventsResponse {
  events: WalletEvent[];
  pagination: {
    nextKey?: string;
    hasMore: boolean;
  };
}

export interface WalletRemoveResponse {
  status: string;
  data: {
    message: string;
    walletId: number;
  };
}

export const createWallet = async (
  masterKey: string,
  userId: string,
  email: string,
  paymentMethod: 'STRIPE' | 'STR' | 'XCH',
  isTest: boolean = STAGE !== 'production'
): Promise<WalletCreateResponse> => {
  try {
    const response = await fetch(`${API_BASE_URL}/wallet/create`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        masterKey,
        userId,
        email,
        paymentMethod,
        isTest,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to create wallet');
    }

    return await response.json();
  } catch (error) {
    console.error('Error creating wallet:', error);
    throw error instanceof Error ? error : new Error('Failed to create wallet');
  }
};

export const getWallets = async (userId: string): Promise<WalletListResponse> => {
  try {
    const response = await fetch(`${API_BASE_URL}/wallet/list?userId=${userId}`);

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to fetch wallets');
    }

    return await response.json();
  } catch (error) {
    throw error instanceof Error ? error : new Error('Failed to fetch wallets');
  }
};

export const getWalletSettings = async (walletId: string, userId: string): Promise<WalletSettings> => {
  try {
    const response = await fetch(`${API_BASE_URL}/wallet/info?walletId=${walletId}&userId=${userId}`);

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to fetch wallet settings');
    }

    const data = await response.json();
    return {
      walletId: data.walletId,
      masterKey: data.masterKey,
      threshold: data.threshold,
      enabled: data.enabled,
      removed: data.removed
    };
  } catch (error) {
    console.error('Error fetching wallet settings:', error);
    throw error instanceof Error ? error : new Error('Failed to fetch wallet settings');
  }
};

export const updateWalletSettings = async (walletId: string, userId: string, updates: WalletSettingsUpdate): Promise<WalletSettingsResponse> => {
  try {
    console.log('Updating wallet settings:', { walletId, userId, updates });
    const response = await fetch(`${API_BASE_URL}/wallet/settings?walletId=${walletId}&userId=${userId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(updates),
    });

    const data = await response.json();
    console.log('Update settings response:', data);

    if (!response.ok) {
      throw new Error(data.message || 'Failed to update wallet settings');
    }

    return data;
  } catch (error) {
    console.error('Error updating wallet settings:', error);
    throw error instanceof Error ? error : new Error('Failed to update wallet settings');
  }
};

export const getWalletInfo = async (walletId: string, userId: string): Promise<WalletInfo> => {
  try {
    const response = await fetch(`${API_BASE_URL}/wallet/info?walletId=${walletId}&userId=${userId}`);

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to fetch wallet info');
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching wallet info:', error);
    throw error instanceof Error ? error : new Error('Failed to fetch wallet info');
  }
};

export const getWalletEvents = async (walletId: string, nextKey?: string): Promise<EventsResponse> => {
  try {
    const url = new URL(`${API_BASE_URL}/wallet/events`);
    url.searchParams.append('walletId', walletId);
    if (nextKey) {
      url.searchParams.append('nextKey', nextKey);
    }

    const response = await fetch(url.toString());
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to fetch wallet events');
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching wallet events:', error);
    throw error instanceof Error ? error : new Error('Failed to fetch wallet events');
  }
};

export const removeWallet = async (walletId: number, userId: string): Promise<WalletRemoveResponse> => {
  try {
    const response = await fetch(`${API_BASE_URL}/wallet/remove`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ walletId, userId })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to remove wallet');
    }

    return await response.json();
  } catch (error) {
    console.error('Error removing wallet:', error);
    throw error instanceof Error ? error : new Error('Failed to remove wallet');
  }
}; 